import range from 'lodash/range';
import datemath from '@elastic/datemath';
import i18next from 'i18next';
import moment from 'moment';
import 'moment-duration-format';
import { useMemo, useReducer, useState } from 'react';

export const preventFutureHoures = (date) => {
  if (date?.isAfter(moment())) return date.startOf('day');
  return date;
};

export const preventFutureProps = {
  hideDisabledOptions: true,
  showNow: false,
  showToday: false,
  allowClear: false,
  inputReadOnly: true,
  disabledDate: (date) => date.isAfter(moment(), 'd'),
  disabledTime: (selected) => {
    const isPrevDay = selected.isBefore(moment(), 'd');
    if (isPrevDay) return {};
    const currentHour = moment().get('hours');
    const currentMinute = moment().get('minutes');
    return ({
      disabledHours: () => range(currentHour + 1, 24, 1),
      disabledMinutes: (selectedHour) => (selectedHour === currentHour ? range(currentMinute + 1, 60, 1) : null),
      disabledSeconds: (selectedHour, selectedMinute) => (
        (selectedHour === currentHour) && (selectedMinute === currentMinute)
          ? range(moment().get('seconds') + 1, 60, 1)
          : null),
    });
  },
};

export const DateTypes = {
  ABS: 'absolute',
  REL: 'relative',
};

export const ErrorTypes = {
  EMPTY: 'empty',
  MINMAX: 'minmax',
};

export const DateFormat = 'DD/MM/YYYY';
export const TimeFormat = 'HH:mm:ss';
export const DateTimeSep = '@';

export const RelPickerOptions = [
  { label: i18next.t('years-ago'), value: '-y' },
  { label: i18next.t('months-ago'), value: '-M' },
  { label: i18next.t('weeks-ago'), value: '-w' },
  { label: i18next.t('days-ago'), value: '-d' },
  { label: i18next.t('hours-ago'), value: '-h' },
  { label: i18next.t('minutes-ago'), value: '-m' },
  { label: i18next.t('seconds-ago'), value: '-s' },

  // { label: i18next.t('years-from-now'), value: '+y' },
  // { label: i18next.t('months-from-now'), value: '+M' },
  // { label: i18next.t('weeks-from-now'), value: '+w' },
  // { label: i18next.t('days-from-now'), value: '+d' },
  // { label: i18next.t('hours-from-now'), value: '+h' },
  // { label: i18next.t('minutes-from-now'), value: '+m' },
  // { label: i18next.t('seconds-from-now'), value: '+s' },
];

export const QuickPickerSignOptions = [
  { label: i18next.t('last'), value: '-' },
  { label: i18next.t('next'), value: '+' },
];

export const QuickPickerCounterTypeOptions = [
  { label: i18next.t('years'), value: 'y' },
  { label: i18next.t('months'), value: 'M' },
  { label: i18next.t('weeks'), value: 'w' },
  { label: i18next.t('days'), value: 'd' },
  { label: i18next.t('hours'), value: 'h' },
  { label: i18next.t('minutes'), value: 'm' },
  { label: i18next.t('seconds'), value: 's' },
];

export const monthToDate = {
  label: i18next.t('month-to-date'),
  start: 'now/M',
  end: 'now',
};
export const absMonthToDate = {
  start: datemath.parse(monthToDate.start).toISOString(),
  end: datemath.parse(monthToDate.end).toISOString(),
};
export const quickAccessOptions = [
  {
    label: i18next.t('day-to-date'),
    start: 'now/d',
    end: 'now',
  },
  // {
  //   label: i18next.t('this-week'),
  //   start: 'now/w',
  //   end: 'now/w',
  // },
  // {
  //   label: i18next.t('this-month'),
  //   start: 'now/M',
  //   end: 'now/M',
  // },
  // {
  //   label: i18next.t('this-year'),
  //   start: 'now/y',
  //   end: 'now/y',
  // },
  {
    label: i18next.t('yesterday'),
    start: 'now-1d/d',
    end: 'now-1d/d',
  },
  {
    label: i18next.t('week-to-date'),
    start: 'now/w',
    end: 'now',
  },
  monthToDate,
  {
    label: i18next.t('year-to-date'),
    start: 'now/y',
    end: 'now',
  },
];

export const getDateToken = (date) => {
  date = moment(date);
  const now = moment();
  const diffInMs = date.diff(now, 'milliseconds', true);
  if (Math.abs(diffInMs) < 1000) return 'now';
  const format = moment.duration(diffInMs, 'milliseconds').format('y[y] M[M] w[w] d[d] h[h] m[m] s[s]', { largest: 1 });
  const largestCounterType = format.slice(-1);
  const diffInLargestCounterType = date.diff(now, largestCounterType, true);
  const fixedDiff = diffInLargestCounterType.toFixed();
  if (+fixedDiff === 0) return 'now';
  const fixedFormat = fixedDiff + largestCounterType;
  const isInThePast = fixedFormat[0] === '-';
  return isInThePast ? `now${fixedFormat}` : `now+${fixedFormat}`;
};

export const getTokenParts = (token, shouldRoundUpOnSlash) => {
  if (!token) return null;
  // resolve the rounding and re create a simple token that we can parse
  const simpleToken = getDateToken(datemath.parse(token, { roundUp: !!shouldRoundUpOnSlash }));
  if (simpleToken === 'now') return { sign: '-', counter: 0, counterType: 's' };
  const tokenRegex = /now([-+])(\d+)(\w)/;
  const parts = tokenRegex.exec(simpleToken);
  const [/* match */, sign, counter, counterType] = parts;
  return { sign, counter: +counter, counterType };
};

export const getDisplayDate = (type, date, shouldRoundUpOnSlash) => {
  if (!type || !date) return null;
  if (type === DateTypes.ABS) return moment(date).format(`${TimeFormat} ${DateTimeSep} ${DateFormat}`);
  if (type === DateTypes.REL) {
    if (date === 'now' || getTokenParts(date, shouldRoundUpOnSlash).counter === 0) return i18next.t('now');
    // fromNow(), works in a similer way to getDateToken but outputs human frindly durations
    return `~ ${datemath.parse(date, { roundUp: !!shouldRoundUpOnSlash }).fromNow()}`;
  }
};

export const getMomentDate = (type, date) => {
  if (type === DateTypes.ABS) return moment(date);
  if (type === DateTypes.REL) {
    if (date) return datemath.parse(date);
    return moment(date);
  }
};

export const getPrevRange = ({
  startType, endType, start, end,
}) => {
  start = getMomentDate(startType, start);
  end = getMomentDate(endType, end);
  const diff = Math.abs(start.diff(end, 'milliseconds'));
  return ({
    start: start.subtract(diff, 'milliseconds').toISOString(),
    end: end.subtract(diff, 'milliseconds').toISOString(),
  });
};

export const getNextRange = ({
  startType, endType, start, end,
}) => {
  start = getMomentDate(startType, start);
  end = getMomentDate(endType, end);
  const diff = Math.abs(start.diff(end, 'milliseconds'));
  return ({
    start: start.add(diff, 'milliseconds').toISOString(),
    end: end.add(diff, 'milliseconds').toISOString(),
  });
};

export const isValidRange = ({
  startType, endType, start, end,
}) => {
  if (!start || !end) return { isValid: false, reason: ErrorTypes.EMPTY };
  if (startType === DateTypes.REL) start = datemath.parse(start);
  if (endType === DateTypes.REL) end = datemath.parse(end, { roundUp: true });
  const formating = 'YYYY-MM-DD HH:mm:ss'; // reformat to remove ms
  const isValid = moment(moment(start).format(formating))
    .isBefore(moment(moment(end).format(formating)));
  if (isValid) return { isValid };
  return { isValid: false, error: ErrorTypes.MINMAX };
};

export const resolveRange = ({ start, end }) => {
  if (start?.startsWith('now')) start = datemath.parse(start);
  if (end?.startsWith('now')) end = datemath.parse(end, { roundUp: true });
  return [moment(start).toISOString(), moment(end).toISOString()];
};

export function useSuperDateState(initRange) {
  const [rangeState, setRangeState] = useReducer(
    (currentState, newState) => ({ ...currentState, ...newState }),
    { start: initRange?.start, end: initRange?.end },
  );

  const fullRangeData = {
    startType: rangeState.start?.startsWith('now') ? DateTypes.REL : DateTypes.ABS,
    endType: rangeState.end?.startsWith('now') ? DateTypes.REL : DateTypes.ABS,
    start: rangeState.start,
    end: rangeState.end,
  };

  const validationState = useMemo(() => isValidRange(fullRangeData), [fullRangeData]);

  const [, updateRoundingDisplyedTokensIfAny] = useReducer(() => Date.now());
  const [quickSelectLabel, setQuickSelectLabel] = useState(initRange?.label);
  const [isRangeStartInitVisible, setIsRangeStartInitVisible] = useState(!!quickSelectLabel);

  return {
    rangeState,
    fullRangeData,
    validationState,
    setRangeState,
    updateRoundingDisplyedTokensIfAny,
    setQuickSelectLabel,
    quickSelectLabel,
    isRangeStartInitVisible,
    setIsRangeStartInitVisible,
  };
}
